import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog'
import { Text } from '@/components/ui/Text'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { LoadingState } from '@/components/ui/StateViews'

export type GuestStepUpPhase = 'sending' | 'enterCode' | 'wait' | 'unavailable'

export interface GuestStepUpDialogProps {
  open: boolean
  phase: GuestStepUpPhase
  onSubmit: (code: string) => void
  onCancel: () => void
  onRetry?: () => void
}

const CODE_LENGTH = 6
const CODE_PATTERN = /^\d{6}$/

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, CODE_LENGTH)
}

function titleFor(phase: GuestStepUpPhase): string {
  switch (phase) {
    case 'sending':
      return 'Sending a new code'
    case 'enterCode':
      return 'Enter the code we sent'
    case 'wait':
      return 'Please wait'
    case 'unavailable':
      return 'Temporarily unavailable'
  }
}

const WAIT_COPY = "You've reached the limit for now. Please wait before trying again."
const UNAVAILABLE_COPY = 'This is temporarily unavailable. Please try again in a moment.'

/**
 * Step-up re-verification inside the guest shell (FR-DF2). Separate from `GuestAuthPanel`
 * because it layers over an already-rendered product surface rather than replacing the panel —
 * same code-entry contract, different chrome (Dialog, focus-trapped, Escape/Cancel dismiss it).
 */
export function GuestStepUpDialog({ open, phase, onSubmit, onCancel, onRetry }: GuestStepUpDialogProps) {
  const titleRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (open) {
      titleRef.current?.focus()
    }
  }, [open, phase])

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      {/* Radix's own autofocus (first tabbable descendant) races the title-focus effect below and
          wins, since it runs as part of Radix's mount/open handling. Preempt it here so the title,
          not the code input or close button, is what receives focus when the dialog opens. */}
      <DialogContent
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          titleRef.current?.focus()
        }}
      >
        {/* aria-live sits on the header wrapper, not the title alone — the wait/unavailable copy in
            DialogDescription is a sibling of the title, not a descendant, so a live region on the
            title only announces the title text and leaves the copy unannounced (same class of bug
            already fixed in GuestAuthPanel.tsx; this dialog needed the same fix separately). */}
        <DialogHeader aria-live="polite" aria-atomic="true">
          <DialogTitle asChild>
            <Text as="h2" variant="title-md" tabIndex={-1} ref={titleRef}>
              {titleFor(phase)}
            </Text>
          </DialogTitle>
          <DialogDescription>
            {phase === 'wait' && WAIT_COPY}
            {phase === 'unavailable' && UNAVAILABLE_COPY}
            {phase === 'enterCode' && 'Enter the 6-digit code from your email to continue.'}
            {phase === 'sending' && 'One moment…'}
          </DialogDescription>
        </DialogHeader>

        {(phase === 'sending' || phase === 'wait') && <LoadingState label="One moment…" />}

        {phase === 'enterCode' && <EnterCodeForm onSubmit={onSubmit} onCancel={onCancel} />}

        {phase === 'unavailable' && (
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            {onRetry && (
              <Button type="button" variant="outline" onClick={onRetry}>
                Try again
              </Button>
            )}
          </DialogFooter>
        )}

        {phase === 'wait' && (
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface EnterCodeFormProps {
  onSubmit: (code: string) => void
  onCancel: () => void
}

/**
 * Its own component (not inlined) so its `code` state mounts fresh every time Radix mounts this
 * subtree — which happens exactly when the dialog opens into the `enterCode` phase, so the field
 * is never pre-populated with a stale code from a previous open. No reset effect needed.
 */
function EnterCodeForm({ onSubmit, onCancel }: EnterCodeFormProps) {
  const [code, setCode] = useState('')

  function submitIfValid(value: string) {
    if (CODE_PATTERN.test(value)) {
      onSubmit(value)
    }
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        submitIfValid(code)
      }}
    >
      <Input
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={CODE_LENGTH}
        pattern="[0-9]{6}"
        aria-label="6-digit code"
        value={code}
        onChange={(e) => setCode(digitsOnly(e.target.value))}
        onPaste={(e) => {
          const pasted = digitsOnly(e.clipboardData.getData('text'))
          if (CODE_PATTERN.test(pasted)) {
            e.preventDefault()
            setCode(pasted)
            submitIfValid(pasted)
          }
        }}
      />
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!CODE_PATTERN.test(code)}>
          Continue
        </Button>
      </DialogFooter>
    </form>
  )
}
